// The PROMOTED LAYER — "always in context" (N-MEM-4), the Signal re-layout: one card per always-in-context
// fact. READ-ONLY here; promotion is its own gated flow and nothing reaches this layer on its own.
//
// WHAT THE DESIGN SHOWS THAT THE DATA DOES NOT SUPPORT, and is therefore ABSENT here:
//   • "why this is in context" — a Promoted entry carries a fact, a category, a rank and its source Node;
//     there is no rationale field, and writing one would be this surface speaking for the Operator;
//   • "In context since <date>" — no promotion timestamp is projected;
//   • "Cited N× this month" — there is no usage signal anywhere in the projection.
// What IS real renders: the category, the rank when one was set, the fact itself, and a link through to the
// source Node when the entry names one.

import { humaniseEnum } from './memory-tree.ts';
import type { PromotedEntryView } from '../know-projections.ts';

/** One always-in-context fact. */
function PromotedCard({
  entry,
  onOpenNode,
}: {
  readonly entry: PromotedEntryView;
  readonly onOpenNode: (nodeId: string) => void;
}): React.JSX.Element {
  const sourceNodeId = entry.sourceNodeId;
  return (
    <div className="mw-card" data-promoted={entry.id}>
      <div className="mw-card-top">
        {entry.rank !== null ? <span className="mw-rank">{entry.rank}</span> : null}
        <span className="mw-eyebrow quiet">{humaniseEnum(entry.category)}</span>
        <span className="spacer" aria-hidden="true" />
        {sourceNodeId !== null ? (
          <button
            type="button"
            className="mw-link"
            aria-label="Open the source memory"
            onClick={() => onOpenNode(sourceNodeId)}
          >
            <span aria-hidden="true">→ </span>
            Open the source memory
          </button>
        ) : null}
      </div>
      <span className="mw-fact">{entry.fact}</span>
    </div>
  );
}

/** The Promoted Layer list — ranked entries first, in the order the owning module recorded them. */
export function MemoryPromotedList({
  promoted,
  onOpenNode,
}: {
  readonly promoted: readonly PromotedEntryView[];
  readonly onOpenNode: (nodeId: string) => void;
}): React.JSX.Element {
  if (promoted.length === 0) {
    return (
      <div className="mw-review" data-review-list="promoted">
        <p className="mw-lead">No promoted facts yet. Nothing reaches this layer on its own.</p>
      </div>
    );
  }
  const unranked = Number.MAX_SAFE_INTEGER;
  const ranked = [...promoted].sort((a, b) => (a.rank ?? unranked) - (b.rank ?? unranked));
  return (
    <div className="mw-review" data-review-list="promoted">
      <p className="mw-lead">
        {promoted.length === 1
          ? 'This memory loads into every conversation Ritona has.'
          : `These ${String(promoted.length)} memories load into every conversation Ritona has.`}{' '}
        Everything else in the tree is pulled in only when it is relevant.
      </p>
      {ranked.map((entry) => (
        <PromotedCard key={entry.id} entry={entry} onOpenNode={onOpenNode} />
      ))}
    </div>
  );
}
